import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown, Search, ArrowRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

// Use static URL from public folder for LCP optimization
const tenacityLogo = '/logo-tenacity.svg';

export const PublicHeader = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Public links shown to everyone
  const publicExploreLinks = [
    { href: '/plans', label: 'Plans' },
    { href: '/about', label: 'About' },
    { href: '/faq', label: 'FAQ' },
    { href: '/contact', label: 'Contact' },
  ];

  // Additional links shown only when logged in
  const memberExploreLinks = [
    { href: '/library', label: 'All Courses' },
    { href: '/artists', label: 'Artists' },
    { href: '/series', label: 'Series' },
  ];

  const exploreLinks = user 
    ? [...memberExploreLinks, ...publicExploreLinks]
    : publicExploreLinks;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/library?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-foreground/20">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src={tenacityLogo} 
              alt="Tenacity Jazz Improv Now" 
              className="h-12 sm:h-14 md:h-16 lg:h-[72px] w-auto"
              fetchPriority="high"
              width={180}
              height={72}
            />
          </Link>
          
          {/* Desktop Navigation - Center */}
          <div className="hidden lg:flex items-center gap-6 flex-1 justify-center max-w-2xl mx-8">
            {/* Explore Dropdown */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger className="flex items-center gap-1.5 text-button text-foreground hover:text-primary transition-colors outline-none">
                EXPLORE
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-card border-border z-50">
                {exploreLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href} className="cursor-pointer text-paragraph-1">
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Search Bar - Only visible when logged in */}
            {user && (
              <form onSubmit={handleSearch} className="flex-1 max-w-md">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search Courses, Artists, or Instruments ..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-card border-border/50 text-foreground placeholder:text-muted-foreground pr-10 h-10"
                  />
                  <button 
                    type="submit"
                    className="absolute right-0 top-0 h-full px-3 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
          
          {/* Desktop Auth Buttons - Right */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="bg-tenacity-gold text-button hover:bg-tenacity-gold/90 rounded-full px-6"
                style={{ color: 'black' }}
              >
                Dashboard
                <ArrowRight className="w-4 h-4 ml-2" style={{ color: 'black' }} />
              </Button>
            ) : (
              <>
                {/* Login Dropdown */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger className="flex items-center gap-1.5 text-button text-foreground hover:text-primary transition-colors outline-none">
                    LOGIN
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-card border-border z-50">
                    <DropdownMenuItem asChild>
                      <Link to="/login" className="cursor-pointer text-paragraph-1">
                        Sign In
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/plans" className="cursor-pointer text-paragraph-1">
                        Create Account
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Join Button */}
                <Button 
                  onClick={() => navigate('/plans')} 
                  className="bg-tenacity-gold text-button hover:bg-tenacity-gold/90 rounded-full px-6"
                  style={{ color: 'black' }}
                >
                  JOIN TENACITY
                  <ArrowRight className="w-4 h-4 ml-2" style={{ color: 'black' }} />
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-foreground" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background border-b border-border/50">
          <div className="px-4 py-4">
            {/* Mobile Search - Only visible when logged in */}
            {user && (
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-card border-border/50 text-foreground placeholder:text-muted-foreground pr-10 text-sm"
                  />
                  <button 
                    type="submit"
                    className="absolute right-0 top-0 h-full px-3 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}

            <nav className="flex flex-col space-y-2">
              {exploreLinks.map((link) => (
                <Link 
                  key={link.href}
                  to={link.href} 
                  className="text-paragraph-1 text-foreground hover:text-primary transition-colors py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="pt-4 mt-4 border-t border-border/50 space-y-3">
              {user ? (
                <Button 
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} 
                  className="w-full bg-tenacity-gold text-button rounded-full"
                  style={{ color: 'black' }}
                >
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" style={{ color: 'black' }} />
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} 
                    variant="outline" 
                    className="w-full border-border text-button"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => { navigate('/plans'); setMobileMenuOpen(false); }} 
                    className="w-full bg-tenacity-gold text-button rounded-full"
                    style={{ color: 'black' }}
                  >
                    JOIN TENACITY
                    <ArrowRight className="w-4 h-4 ml-2" style={{ color: 'black' }} />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
